import API from "../../services/api";
import { idempotencyHeaders } from "../../utils/idempotency";

/**
 * Enterprise employee-banking API client.
 *
 * All account numbers are encrypted at rest — the API only returns ``account_number_last4``
 * and ``account_number_masked``. Callers should render masks, never attempt to reconstruct
 * the full number on the client.
 */

export function listAccounts(employeeId, { includeInactive = false } = {}) {
  return API.get(`/employee-banking/employees/${employeeId}/accounts`, {
    params: includeInactive ? { include_inactive: true } : undefined,
  }).then((r) => r.data);
}

export function createAccount(payload, { idempotencyKey } = {}) {
  return API.post("/employee-banking/accounts", payload, {
    headers: idempotencyHeaders("emp-bank-create", idempotencyKey),
  }).then((r) => r.data);
}

export function updateAccount(bankAccountId, payload, { idempotencyKey } = {}) {
  return API.put(`/employee-banking/accounts/${bankAccountId}`, payload, {
    headers: idempotencyHeaders("emp-bank-update", idempotencyKey),
  }).then((r) => r.data);
}

export function deactivateAccount(bankAccountId) {
  return API.delete(`/employee-banking/accounts/${bankAccountId}`).then(
    (r) => r.data
  );
}

export function verifyAccount(bankAccountId, { status, comment } = {}) {
  return API.post(`/employee-banking/accounts/${bankAccountId}/verify`, {
    status,
    comment,
  }).then((r) => r.data);
}

export function checkDuplicate({ employeeId, bankBranchId, accountNumber }) {
  return API.post("/employee-banking/accounts/duplicate-check", {
    employee_id: employeeId,
    bank_branch_id: bankBranchId,
    account_number: accountNumber,
  }).then((r) => r.data);
}

export function validateAccountNumber(accountNumber) {
  return API.post("/employee-banking/accounts/validate", {
    account_number: accountNumber,
  }).then((r) => r.data);
}

export function validateIfsc(ifscCode) {
  return API.post("/employee-banking/accounts/validate-ifsc", {
    ifsc_code: ifscCode,
  }).then((r) => r.data);
}

/* ─── Change requests (maker-checker) ─────────────────────────────────────── */
export function submitChangeRequest(payload, { idempotencyKey } = {}) {
  return API.post("/employee-banking/change-requests", payload, {
    headers: idempotencyHeaders("emp-bank-change-req", idempotencyKey),
  }).then((r) => r.data);
}

export function listChangeRequests({ status = "pending", limit = 100 } = {}) {
  return API.get("/employee-banking/change-requests", {
    params: { status_filter: status, limit },
  }).then((r) => r.data);
}

export function listEmployeeChangeRequests(employeeId) {
  return API.get(
    `/employee-banking/employees/${employeeId}/change-requests`
  ).then((r) => r.data);
}

export function reviewChangeRequest(
  requestId,
  { decision, reviewerComment } = {}
) {
  return API.post(`/employee-banking/change-requests/${requestId}/review`, {
    decision,
    reviewer_comment: reviewerComment,
  }).then((r) => r.data);
}

/* ─── Bank branch lookup passthrough (existing endpoint) ──────────────────── */
export function listBanks() {
  return API.get("/banking/banks").then((r) => r.data);
}

export function listBranches(bankId) {
  return API.get(`/banking/banks/${bankId}/branches`).then((r) => r.data);
}

/** Resolve an IFSC to bank + branch via the live directory (cached server-side).
 *  Returns { bank_id, bank_name, branch_id, branch_name, ifsc_code, city, state, ... }. */
export function resolveIfsc(ifscCode) {
  return API.get(`/banking/ifsc/${encodeURIComponent(ifscCode.trim().toUpperCase())}`).then(
    (r) => r.data
  );
}
